import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerAccountDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { Shell } from './RefillDialog';

interface CustomerFormDialogProps {
  /** Null to register a new account (§5.5), otherwise edit this one. */
  customer: CustomerAccountDto | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FieldError {
  field: string;
  message: string;
}

/**
 * Register or edit a customer account — SPEC §5.5.
 *
 * Balance and points are deliberately absent. They move only through a Refill,
 * a Balance Refund or a payment, each of which writes its own audited row; an
 * editable balance field here would be an unaudited way to mint money.
 */
export function CustomerFormDialog({ customer, onClose, onSaved }: CustomerFormDialogProps) {
  const { t } = useTranslation();

  const [fullName, setFullName] = useState(customer?.fullName ?? '');
  const [phone, setPhone] = useState(customer?.phone ?? '');
  const [email, setEmail] = useState(customer?.email ?? '');
  const [username, setUsername] = useState(customer?.username ?? '');
  const [password, setPassword] = useState('');
  const [cardId, setCardId] = useState(customer?.cardId ?? '');
  // Wave 3 (§5.6): the discount is split into three buckets — reservations
  // (play time), cafe items and kitchen items — replacing the old single
  // percent, which the checkout never actually applied.
  const [discountTimePercent, setDiscountTimePercent] = useState(
    String(customer?.discountTimePercent ?? 0),
  );
  const [discountCafePercent, setDiscountCafePercent] = useState(
    String(customer?.discountCafePercent ?? 0),
  );
  const [discountKitchenPercent, setDiscountKitchenPercent] = useState(
    String(customer?.discountKitchenPercent ?? 0),
  );
  const [discountShishaPercent, setDiscountShishaPercent] = useState(
    String(customer?.discountShishaPercent ?? 0),
  );
  const [note, setNote] = useState(customer?.note ?? '');

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const errorFor = (field: string) => errors.find((error) => error.field === field)?.message;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);

    // Empty strings are cleared fields, not blanks: an edit sends null so the
    // column is emptied, while a create simply omits them.
    const optional = (value: string) => (value.trim() ? value.trim() : customer ? null : undefined);

    const payload = {
      fullName: fullName.trim(),
      phone: optional(phone),
      email: optional(email),
      username: optional(username),
      ...(password ? { password } : {}),
      cardId: optional(cardId),
      discountTimePercent: Number(discountTimePercent) || 0,
      discountCafePercent: Number(discountCafePercent) || 0,
      discountKitchenPercent: Number(discountKitchenPercent) || 0,
      discountShishaPercent: Number(discountShishaPercent) || 0,
      note: optional(note),
    };

    try {
      if (customer) await api.patch(`/customers/${customer.id}`, payload);
      else await api.post('/customers', payload);
      onSaved();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError && Array.isArray(caught.details)) {
        setErrors(caught.details as FieldError[]);
      } else {
        setErrors([
          {
            field: '(root)',
            message: caught instanceof ApiError ? caught.message : t('customers.saveFailed'),
          },
        ]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';
  const title = customer ? t('customers.editCustomer') : t('customers.newCustomer');

  return (
    <Shell label={title} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)}>
        <h2 className="mb-4 text-lg font-semibold text-content">{title}</h2>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            className="sm:col-span-2"
            label={t('customers.fullName')}
            error={errorFor('fullName')}
          >
            <input
              required
              autoFocus
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              className={inputClass}
            />
          </Field>

          <Field label={t('common.phoneNumber')} error={errorFor('phone')}>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className={inputClass}
              // Stored exactly as typed: 0770… and +964770… are both what some
              // customer will say, and normalising one into the other makes the
              // number they recognise un-searchable.
              inputMode="tel"
            />
          </Field>

          <Field label={t('customers.email')} error={errorFor('email')}>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={inputClass}
            />
          </Field>

          <Field label={t('common.username')} error={errorFor('username')}>
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              className={inputClass}
            />
          </Field>

          <Field
            label={t('customers.portalPassword')}
            error={errorFor('password')}
            hint={
              customer?.hasPortalLogin
                ? t('customers.portalPasswordSet')
                : t('customers.portalPasswordHint')
            }
          >
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={inputClass}
            />
          </Field>

          <Field
            label={t('customers.cardId')}
            error={errorFor('cardId')}
            hint={t('customers.cardIdHint')}
          >
            <input
              value={cardId}
              onChange={(event) => setCardId(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </Field>

          <Field
            label={t('customers.discountTimePercent')}
            error={errorFor('discountTimePercent')}
            hint={t('customers.discountHint')}
          >
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={discountTimePercent}
              onChange={(event) => setDiscountTimePercent(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </Field>

          <Field label={t('customers.discountCafePercent')} error={errorFor('discountCafePercent')}>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={discountCafePercent}
              onChange={(event) => setDiscountCafePercent(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </Field>

          <Field
            label={t('customers.discountKitchenPercent')}
            error={errorFor('discountKitchenPercent')}
          >
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={discountKitchenPercent}
              onChange={(event) => setDiscountKitchenPercent(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </Field>

          <Field
            label={t('customers.discountShishaPercent')}
            error={errorFor('discountShishaPercent')}
          >
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={discountShishaPercent}
              onChange={(event) => setDiscountShishaPercent(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </Field>

          <Field className="sm:col-span-2" label={t('customers.note')} error={errorFor('note')}>
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={500}
              className={inputClass}
            />
          </Field>
        </div>

        {errorFor('(root)') ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {errorFor('(root)')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </Shell>
  );
}

function Field({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="mb-1 block text-sm text-content-secondary">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : null}
      {!error && hint ? (
        <span className="mt-1 block text-xs text-content-muted">{hint}</span>
      ) : null}
    </label>
  );
}
